// @perlab/cross-tab — Envelope + Hybrid Logical Clock (HLC).
//
// Verbatim per spec §3.2.1 (28_Parallel_Work_Architecture_Tier1_V3.md):
//   nextHlc() = max(localHlc + 1, Date.now())
//   observeRemoteHlc(remote) = max(localHlc, remote) + 1
//
// Resolves HIGH N2 (subsumes M1 + M5). Property: late-joiner tabs starting
// with localHlc = 0 immediately fast-forward on first received message —
// no "always loses" pathology. Total order is (hlc, tabId) — lexicographic
// tabId breaks ties. See §3.2.6 for conflict-resolution use.

/**
 * Cross-tab bus envelope. Every message on the bus is wrapped in one of these.
 *
 * - `type`     — message kind ('theme.changed', 'tab.hello', ...). Allowlists
 *                in replay.ts classify these as snapshot vs event.
 * - `tabId`    — originator tab id; receivers ignore env.tabId === self.
 * - `hlc`      — Hybrid Logical Clock; primary ordering key.
 * - `ts`       — wall-clock ms; advisory only (skew across tabs is real).
 * - `tenantId` — required; subscriber drops on mismatch (§3.2.3 fail-closed).
 * - `userId`   — required; same drop rule.
 * - `payload`  — typed message body.
 */
export interface BusEnvelope<T = unknown> {
  type: string;
  tabId: string;
  hlc: number;
  ts: number;
  tenantId: string;
  userId: string;
  payload: T;
}

// Module-local HLC state. One per JS realm (per tab). NOT shared across tabs —
// each tab maintains its own HLC and the merge happens on receive.
let localHlc = 0;

/**
 * Advance HLC for a new local publish.
 *
 * Spec §3.2.1 verbatim:
 *   localHlc = Math.max(localHlc + 1, Date.now())
 *
 * Ensures monotonicity even when Date.now() goes backwards (clock skew, NTP
 * step) AND ensures HLC tracks wall clock when system runs faster than
 * publishes.
 */
export function nextHlc(): number {
  const nowMs = Date.now();
  localHlc = Math.max(localHlc + 1, nowMs);
  return localHlc;
}

/**
 * Merge an HLC observed in an incoming envelope into local state.
 *
 * Spec §3.2.1 verbatim:
 *   localHlc = Math.max(localHlc, remote) + 1
 *
 * Called by the bus receive path BEFORE handler dispatch. Guarantees that
 * any subsequent nextHlc() in this tab will issue an HLC strictly greater
 * than any HLC this tab has ever seen — convergence property.
 */
export function observeRemoteHlc(remote: number): void {
  localHlc = Math.max(localHlc, remote) + 1;
}

/**
 * Read the current HLC without advancing. Used by tests + devtools only.
 * Production code should call nextHlc() (publish path) or observeRemoteHlc()
 * (receive path) — never read raw.
 */
export function peekHlc(): number {
  return localHlc;
}

/**
 * Reset HLC to zero. Test-only — exposed for unit tests that need a clean
 * baseline across cases. Production code MUST NOT call this.
 */
export function __resetHlcForTests(): void {
  localHlc = 0;
}

/**
 * Total-order comparator for two envelopes per §3.2.6.
 *
 * Returns:
 *   < 0 — `a` is older (a should apply first)
 *   > 0 — `b` is older
 *   = 0 — only possible when a.tabId === b.tabId AND a.hlc === b.hlc; treated
 *         as equal (callers MUST treat same-tab+same-hlc as the same event).
 */
export function compareEnvelopes(a: BusEnvelope, b: BusEnvelope): number {
  if (a.hlc !== b.hlc) return a.hlc - b.hlc;
  if (a.tabId < b.tabId) return -1;
  if (a.tabId > b.tabId) return 1;
  return 0;
}
